/**
 * @file Recalls limited inline editing and status intercept modals.
 */

import { appData } from "../../app/state/state.js";
import {
    FIXED_TEXTAREA_CLASSES,
    getNativeFieldWrapperHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    completeRecallStatusWorkflow,
    updateRecallEntry,
} from "./recallsApi.js";
import { getRecallProviderDisplay } from "./recallsProviderUtils.js";
import { getRecallReasonDisplay } from "./recallsReasonUtils.js";
import {
    loadRecallsMonthBucket,
} from "./recallsDataListeners.js";
import {
    applyRecallStatusMutationToRouteCaches,
    recallsRouteState,
} from "./recallsState.js";
import {
    RECALLS_MODAL_GRID_CLASSES,
    RECALLS_MODAL_STACK_CLASSES,
} from "./recallsModalLayout.js";
import {
    openRecallsConfigIncompleteModal,
} from "./recallsConfigIncomplete.js";

const INLINE_STATUS_VALUE_BUTTON_CLASS = [
    "group inline-flex min-h-7 max-w-[12rem] items-center gap-1.5 rounded px-0 py-0.5",
    "text-left text-xs text-gray-700 focus:outline-none",
].join(" ");
const INLINE_STATUS_VALUE_TEXT_CLASS = "min-w-0 overflow-hidden whitespace-nowrap group-hover:underline";
const INLINE_STATUS_EDITOR_CLASS = [
    "w-full",
    "[&_button]:min-h-7 [&_button]:px-1.5 [&_button]:py-0",
    "[&_button]:text-xs [&_button_span.material-symbols-outlined]:text-[17px]",
].join(" ");
const WORKFLOW_ROLES = new Set(["declined", "cancelled", "rescheduled"]);

let inlineEditRerender = null;

function attrValue(value) {
    return escapeHTML(String(value ?? ""));
}

function getAllRecallsRows() {
    return [...(appData.recalls || []), ...(appData.closedRecalls || [])];
}

function getRecallById(recallId) {
    return getAllRecallsRows().find((item) => item.id === recallId) || null;
}

function getStatusRole(statusId) {
    const mappings = appData.config?.recalls?.statusRoleMappings || {};
    return Object.entries(mappings)
        .find(([, mappedStatusId]) => mappedStatusId === statusId)?.[0] || null;
}

function getOptionList(key) {
    return (appData.config?.recalls?.[key] || [])
        .filter((option) => option?.id && option.isActive !== false);
}

function getWorkflowReasonOptions(role) {
    if (role === "declined") {return getOptionList("declinedReasons");}
    if (role === "cancelled") {return getOptionList("cancelledReasons");}
    return getOptionList("rescheduleReasons");
}

function getStatusOptions(currentStatusId) {
    return (appData.config?.recallStatus || [])
        .filter((option) =>
            option?.id &&
            (option.active !== false || option.id === currentStatusId));
}

function getCallableErrorMessage(error) {
    return error?.message || "The Recalls action could not be completed.";
}

function showErrorModal(title, message) {
    openConfirmationModal(
        title,
        escapeHTML(message),
        async () => {},
        false,
        "OK",
    );
}

function setControlBusy(control, busy) {
    if (typeof control?.update === "function") {
        control.update({ disabled: busy });
    }
    const root = control?.root || control;
    root?.querySelectorAll?.("button, input, select, textarea")
        .forEach((element) => {
            element.disabled = busy;
            element.classList.toggle("opacity-70", busy);
        });
}

function getOptionLabel(options, value, fallback = "Unassigned") {
    const option = options.find((entry) => entry.id === value);
    return option?.name || option?.label || value || fallback;
}

function getStatusLabel(value) {
    return getOptionLabel(getStatusOptions(value), value, "Unassigned");
}

function getDuplicateRecallName(match = {}) {
    return [match.first_name, match.last_name]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" ") ||
        match.email ||
        "Unnamed recall";
}

function formatStoredDateForWarning(dateString) {
    if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateString))) {
        return "";
    }
    const [, year, month, day] = String(dateString)
        .match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
    return year && month && day ? `${day}/${month}/${year}` : "";
}

function formatTimestampDateForWarning(value) {
    if (!value) {return "";}
    const date = value?.toDate ? value.toDate() :
        typeof value.seconds === "number" ?
            new Date(value.seconds * 1000) :
            typeof value._seconds === "number" ?
                new Date(value._seconds * 1000) :
                new Date(value);
    if (Number.isNaN(date.getTime())) {return "";}
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
}

function getDuplicateMatchRows(response = {}) {
    return [
        ...(response.duplicates?.active || []),
        ...(response.duplicates?.recentlyClosed || []),
    ];
}

function getMatchedByLabel(matchFields = []) {
    const labels = (Array.isArray(matchFields) ? matchFields : [])
        .map((field) => {
            if (field === "email") {return "Email";}
            if (field === "phone") {return "Phone";}
            return field;
        })
        .filter(Boolean);
    if (labels.length === 0) {return "Contact details";}
    if (labels.length === 1) {return labels[0];}
    return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

function renderDuplicateMatchCard(match = {}, options = {}) {
    const recallDate = formatStoredDateForWarning(match.recall_date) ||
        match.recall_date ||
        "No recall date";
    const reason = getRecallReasonDisplay(
        match.recall_reason,
        appData.config?.recalls?.recallReasons || [],
    ) || "No reason";
    const provider = getRecallProviderDisplay(match.provider) || "No provider";
    const closedDate = formatTimestampDateForWarning(match.closedAt);
    const newRecallDate = formatStoredDateForWarning(options.newRecallDate);
    const dateRows = options.recentlyClosed ? [
        ["New recall date", newRecallDate || "No recall date"],
        ["Recently closed recall", recallDate],
        ["Closed date", closedDate || "No closed date"],
    ] : [
        ["Recall date", recallDate],
    ];
    const rows = [
        ["Matched by", getMatchedByLabel(match.matchFields)],
        ...dateRows,
        ["Status", getStatusLabel(match.status)],
        ["Reason", reason],
        ["Provider", provider],
    ];

    return `
        <div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div class="font-semibold text-gray-800">${escapeHTML(getDuplicateRecallName(match))}</div>
            <dl class="mt-2 space-y-1 text-xs text-gray-500">
                ${rows.map(([label, value]) => `
                    <div>
                        <dt class="inline font-medium text-gray-600">${escapeHTML(label)}:</dt>
                        <dd class="inline">${escapeHTML(value)}</dd>
                    </div>
                `).join("")}
            </dl>
        </div>
    `;
}

function renderDuplicateWarningBody(response = {}) {
    const matches = getDuplicateMatchRows(response);
    const activeMatches = response.duplicates?.active || [];
    const recentlyClosedMatches = response.duplicates?.recentlyClosed || [];
    if (matches.length === 0) {
        return `
            <div class="space-y-2 text-gray-600">
                <p>${escapeHTML(response.message || "A replacement recall may already exist.")}</p>
                <p>Do you want to create it anyway?</p>
            </div>
        `;
    }

    return `
        <div class="space-y-3 text-gray-600">
            <p>${escapeHTML(response.blocked === true ?
                "A recall with matching contact details already exists in the same recall month." :
                "A recall with matching contact details may already exist.")}</p>
            <div class="space-y-2">
                ${(response.blocked === true &&
                    response.duplicates?.blockingActive?.length ?
                        response.duplicates.blockingActive :
                        activeMatches).map((match) =>
                    renderDuplicateMatchCard(match)).join("")}
                ${recentlyClosedMatches.map((match) =>
                    renderDuplicateMatchCard(match, {
                        recentlyClosed: true,
                        newRecallDate: response.recallDate,
                    })).join("")}
            </div>
            ${response.blocked === true ? "" : "<p>Do you want to create it anyway?</p>"}
        </div>
    `;
}

function renderInlineStatusButton(item = {}) {
    const label = getStatusLabel(item.status);
    return `
        <span
            class="relative inline-block w-full max-w-full"
            data-recalls-inline-root
            data-recalls-inline-field="status"
            data-recall-id="${attrValue(item.id)}"
        >
            <button
                type="button"
                class="${INLINE_STATUS_VALUE_BUTTON_CLASS}"
                data-recalls-inline-activate
                title="Edit ${attrValue(label)}"
                aria-label="Edit ${attrValue(label)}"
            >
                <span class="${INLINE_STATUS_VALUE_TEXT_CLASS}" title="${attrValue(label)}">${escapeHTML(label)}</span>
            </button>
        </span>
    `;
}

function getSelectOptionList(options) {
    return options.map((option) => ({
        value: option.id,
        label: option.name || option.label || option.id,
        source: option,
    }));
}

function createInlineEditorHandle(controlHandle) {
    return {
        root: controlHandle.root,
        update: (updates) => controlHandle.update(updates),
        get value() {
            return controlHandle.value;
        },
        set value(value) {
            controlHandle.setValue(value, { silent: true });
        },
    };
}

function openInlineSelect(root) {
    requestAnimationFrame(() => {
        const trigger = root.querySelector("[aria-haspopup=\"listbox\"], [role=\"combobox\"]");
        trigger?.focus?.({ preventScroll: true });
        trigger?.click?.();
    });
}

function bindInlineRoot(root) {
    if (!root || root.dataset.recallsInlineBound === "true") {return;}
    root.dataset.recallsInlineBound = "true";
    root.querySelector("[data-recalls-inline-activate]")
        ?.addEventListener("click", () => activateInlineEditor(root));
}

function replaceInlineRoot(root, html) {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    const nextRoot = template.content.firstElementChild;
    if (!nextRoot) {return root;}
    root.replaceWith(nextRoot);
    bindInlineRoot(nextRoot);
    return nextRoot;
}

function renderInlineFieldRoot(field, item) {
    if (field !== "status") {return "";}
    return renderRecallStatusInlineControl(item);
}

function restoreInlineDisplay(root, field, item) {
    return replaceInlineRoot(root, renderInlineFieldRoot(field, item));
}

function bindNoopStatusBlurRestore(root, handle, recall) {
    let readyForBlurRestore = false;
    window.setTimeout(() => {
        readyForBlurRestore = true;
    }, 200);

    const restoreIfUnchanged = ({ ignoreFocus = false } = {}) => {
        window.setTimeout(() => {
            if (!readyForBlurRestore) {return;}
            if (!root.isConnected || handle.value !== (recall.status || "")) {return;}
            if (!ignoreFocus && root.contains(document.activeElement)) {return;}
            restoreInlineDisplay(root, "status", recall);
        }, 120);
    };

    root.addEventListener("focusout", restoreIfUnchanged);
    document.addEventListener("pointerdown", (event) => {
        if (root.contains(event.target)) {return;}
        if (event.target?.closest?.("[role=\"listbox\"]")) {return;}
        restoreIfUnchanged({ ignoreFocus: true });
    }, { once: true });
}

function activateStatusEditor(root, recall) {
    root.replaceChildren();
    root.classList.add(...INLINE_STATUS_EDITOR_CLASS.split(" "));
    root.style.color = "#374151";
    const handle = createSelectControl({
        root,
        name: "status",
        value: recall.status || "",
        options: getSelectOptionList(getStatusOptions(recall.status)),
        placeholder: "+ Add status",
        portalMenu: true,
        clearable: false,
        onChange: () => {
            handleStatusChange(createInlineEditorHandle(handle), recall, root);
        },
    });
    bindNoopStatusBlurRestore(root, handle, recall);
    openInlineSelect(root);
}

function activateInlineEditor(root) {
    if (!root || root.dataset.inlineEditorActive === "true") {return;}
    const recall = getRecallById(root.dataset.recallId);
    const field = root.dataset.recallsInlineField;
    if (!recall?.id || field !== "status") {return;}

    root.dataset.inlineEditorActive = "true";
    activateStatusEditor(root, recall);
}

/**
 * Renders the inline status editor.
 * @param {object} item Recall row.
 * @returns {string} Status select HTML.
 */
export function renderRecallStatusInlineControl(item = {}) {
    return renderInlineStatusButton(item);
}

async function saveInlinePatch(control, recall, patch, options = {}) {
    setControlBusy(control, true);
    try {
        const response = await updateRecallEntry({
            recallId: recall.id,
            patch,
            expectedUpdatedAt: recall.updatedAt || null,
        });
        await options.onSuccess?.(response);
    } catch (error) {
        options.onError?.(error);
        if (patch.status) {
            control.value = recall.status || "";
        }
        if (openRecallsConfigIncompleteModal(error)) {return;}
        showErrorModal("Recalls Update", getCallableErrorMessage(error));
    } finally {
        setControlBusy(control, false);
    }
}

function getClosedRefreshMonthKey(recall = {}) {
    if (recall.closedMonth) {return recall.closedMonth;}
    if (recall.effectiveMonth) {return recall.effectiveMonth;}
    const dateValue = recall.email_date || recall.recall_date || "";
    return /^\d{4}-\d{2}/.test(dateValue) ? dateValue.slice(0, 7) : null;
}

async function refreshClosedRecallMonth(recall = {}) {
    if (recall.isClosedResolved !== true) {return;}
    if (recallsRouteState.showClosed !== true) {return;}
    const monthKey = getClosedRefreshMonthKey(recall);
    if (!monthKey) {return;}
    await loadRecallsMonthBucket(monthKey, {
        force: true,
        includeActive: true,
        onData: inlineEditRerender,
        showClosed: true,
    });
}

async function handleConfirmedStatusMutation(recall = {}, targetStatusId) {
    const updatedRecall = applyRecallStatusMutationToRouteCaches(
        recall.id,
        targetStatusId,
    );
    inlineEditRerender?.();
    if (updatedRecall) {
        await refreshClosedRecallMonth(updatedRecall);
    }
}

async function refreshReplacementRecallMonth(response = {}, workflowData = {}) {
    if (!response?.replacementRecallId) {return;}
    const recallDate = workflowData.recall_date || "";
    const monthKey = /^\d{4}-\d{2}/.test(recallDate) ?
        recallDate.slice(0, 7) :
        "";
    if (!monthKey) {return;}
    await loadRecallsMonthBucket(monthKey, {
        force: true,
        includeActive: true,
        onData: inlineEditRerender,
        showClosed: recallsRouteState.showClosed,
    });
}

function saveStatusPatchOptimistically(control, recall, targetStatusId, root) {
    const optimisticItem = {
        ...recall,
        status: targetStatusId,
    };
    const displayRoot = restoreInlineDisplay(root, "status", optimisticItem);
    saveInlinePatch(control, recall, { status: targetStatusId }, {
        onError: () => {
            restoreInlineDisplay(displayRoot, "status", recall);
        },
        onSuccess: () => handleConfirmedStatusMutation(recall, targetStatusId),
    });
}

function getWorkflowTitle(role) {
    if (role === "declined") {return "Decline Recall";}
    if (role === "cancelled") {return "Cancel Recall";}
    return "Reschedule Recall";
}

function getWorkflowReasonName(role) {
    if (role === "declined") {return "declinedReason";}
    if (role === "cancelled") {return "cancelledReason";}
    return "rescheduleReason";
}

function renderWorkflowModalBody(role) {
    const reasonField = getNativeFieldWrapperHtml("Reason", `
        <div data-recalls-workflow-reason-control></div>
        <input type="hidden" value="" data-recalls-workflow-reason>
    `, { required: true });
    const dateField = role === "rescheduled" ?
        getNativeFieldWrapperHtml("New Recall Date", `
            <div data-recalls-workflow-date-control></div>
            <input type="hidden" value="" data-recalls-workflow-date-mirror>
        `, { required: true }) :
        "";

    return `
        <div class="${RECALLS_MODAL_STACK_CLASSES} px-1">
            ${role === "rescheduled" ? `
                <div class="${RECALLS_MODAL_GRID_CLASSES}">
                    ${reasonField}
                    ${dateField}
                </div>
            ` : reasonField}
            ${getNativeFieldWrapperHtml("Notes", `
                <textarea
                    rows="3"
                    class="${FIXED_TEXTAREA_CLASSES}"
                    data-recalls-workflow-notes
                ></textarea>
            `)}
            <p class="text-sm text-red-700" data-recalls-workflow-error></p>
        </div>
    `;
}

function getWorkflowReasonControlOptions(role) {
    return getWorkflowReasonOptions(role).map((option) => ({
        value: option.id,
        label: option.name || option.label || option.id,
    }));
}

function mountWorkflowModalControls(role) {
    const modal = document.getElementById("confirm-modal");
    const reasonRoot = modal?.querySelector("[data-recalls-workflow-reason-control]");
    const reasonMirror = modal?.querySelector("[data-recalls-workflow-reason]");
    if (reasonRoot && reasonMirror && !reasonRoot.dataset.controlId) {
        createSelectControl({
            root: reasonRoot,
            mirrorInput: reasonMirror,
            name: getWorkflowReasonName(role),
            options: getWorkflowReasonControlOptions(role),
            placeholder: "Select reason",
            accessibleName: "Workflow reason",
            clearable: false,
            portalMenu: true,
            required: true,
        });
    }

    const dateRoot = modal?.querySelector("[data-recalls-workflow-date-control]");
    const dateMirror = modal?.querySelector("[data-recalls-workflow-date-mirror]");
    if (dateRoot && dateMirror && !dateRoot.dataset.controlId) {
        createDateControl({
            root: dateRoot,
            mirrorInput: dateMirror,
            name: "recall_date",
            required: true,
            onChange: () => tagWorkflowDateInput(dateRoot),
        });
        tagWorkflowDateInput(dateRoot);
    }
}

function tagWorkflowDateInput(root) {
    root.querySelector("input[type=\"date\"]")
        ?.setAttribute("data-recalls-workflow-date", "");
}

function collectWorkflowData(role, modal) {
    const reasonId = modal.querySelector("[data-recalls-workflow-reason]")?.value;
    const notes = modal.querySelector("[data-recalls-workflow-notes]")?.value.trim();
    const recallDate = modal.querySelector("[data-recalls-workflow-date-mirror]")?.value;
    const workflowData = {
        [getWorkflowReasonName(role)]: reasonId || null,
        notes: notes || null,
    };
    if (role === "rescheduled") {
        workflowData.recall_date = recallDate || null;
    }
    return workflowData;
}

function getWorkflowValidationMessage(role, workflowData) {
    if (!workflowData[getWorkflowReasonName(role)]) {
        return "Choose a reason before continuing.";
    }
    if (role === "rescheduled" && !workflowData.recall_date) {
        return "Choose a new recall date before continuing.";
    }
    return "";
}

function completeWorkflow({
    control,
    duplicateContext = null,
    duplicateOverride = false,
    recall,
    targetStatusId,
    workflowData,
}) {
    return completeRecallStatusWorkflow({
        duplicateContext,
        duplicateOverride,
        expectedUpdatedAt: recall.updatedAt || null,
        recallId: recall.id,
        targetStatusId,
        workflowData,
    }).then(async (response) => {
        if (response?.success === false && response.type === "duplicate_warning") {
            window.setTimeout(() => {
                openDuplicateWorkflowWarning({
                    control,
                    duplicateContext,
                    recall,
                    response,
                    targetStatusId,
                    workflowData,
                });
            }, 0);
            return;
        }
        if (response?.success === false) {
            throw new Error(response.message || "The workflow was not completed.");
        }
        await handleConfirmedStatusMutation(recall, targetStatusId);
        await refreshReplacementRecallMonth(response, workflowData);
    });
}

function mergeDuplicateOverrideContext(currentContext, nextContext) {
    if (!nextContext?.scope) {return nextContext || currentContext || null;}
    const merged = currentContext && !currentContext.scope ?
        { ...currentContext } :
        currentContext?.scope ?
            { [currentContext.scope]: currentContext } :
            {};
    merged[nextContext.scope] = nextContext;
    return merged;
}

function openDuplicateWorkflowWarning({
    control,
    duplicateContext = null,
    recall,
    response,
    targetStatusId,
    workflowData,
}) {
    const role = getStatusRole(targetStatusId);
    if (response.blocked === true) {
        openConfirmationModal(
            "Duplicate Recall Blocked",
            renderDuplicateWarningBody(response),
            async () => {},
            false,
            "OK",
            () => {
                control.value = recall.status || "";
            },
            "OK",
            "Cancel",
            { maxWidth: "sm", scrollBody: true },
        );
        return;
    }

    const hasActive = (response.duplicates?.active || []).length > 0;
    const hasRecentlyClosed =
        (response.duplicates?.recentlyClosed || []).length > 0;
    openConfirmationModal(
        hasRecentlyClosed && !hasActive ?
            "Recently Closed Recall" :
            "Possible Duplicate Recall",
        renderDuplicateWarningBody(response),
        async () => {
            try {
                const nextDuplicateContext = mergeDuplicateOverrideContext(
                    duplicateContext,
                    response.duplicateContext,
                );
                await completeWorkflow({
                    control,
                    duplicateContext: nextDuplicateContext,
                    duplicateOverride: true,
                    recall,
                    targetStatusId,
                    workflowData,
                });
            } catch (error) {
                control.value = recall.status || "";
                if (openRecallsConfigIncompleteModal(error)) {return;}
                showErrorModal("Recalls Workflow", getCallableErrorMessage(error));
            }
        },
        false,
        role === "rescheduled" ? "Reschedule anyway" : "Save anyway",
        () => {
            control.value = recall.status || "";
        },
        "Saving...",
        "Cancel",
        { maxWidth: "sm", scrollBody: true },
    );
}

function openWorkflowModal({ control, recall, role, targetStatusId }) {
    openConfirmationModal(
        getWorkflowTitle(role),
        renderWorkflowModalBody(role),
        async () => {
            const modal = document.getElementById("confirm-modal");
            const errorEl = modal?.querySelector("[data-recalls-workflow-error]");
            const workflowData = collectWorkflowData(role, modal);
            const validationMessage = getWorkflowValidationMessage(role, workflowData);
            if (validationMessage) {
                if (errorEl) {errorEl.textContent = validationMessage;}
                return false;
            }

            try {
                await completeWorkflow({
                    control,
                    recall,
                    targetStatusId,
                    workflowData,
                });
                return true;
            } catch (error) {
                control.value = recall.status || "";
                if (openRecallsConfigIncompleteModal(error)) {
                    return false;
                }
                if (errorEl) {
                    errorEl.textContent = getCallableErrorMessage(error);
                }
                return false;
            }
        },
        false,
        role === "rescheduled" ? "Reschedule" : "Save",
        () => {
            control.value = recall.status || "";
        },
        "Saving...",
        "Cancel",
        {
            actionsLayout: "right",
            closeOnError: false,
            contentInset: "px-1",
            keepOpenOnFalse: true,
            maxWidth: role === "rescheduled" ? "2xl" : "sm",
            scrollBody: true,
        },
    );
    const title = document.getElementById("confirm-title");
    title?.classList.remove("text-lg");
    title?.classList.add("text-xl", "leading-snug", "sm:text-2xl");
    mountWorkflowModalControls(role);
}

function handleStatusChange(control, recall, root = control.root) {
    const targetStatusId = control.value;
    if (targetStatusId === recall.status) {return;}

    const role = getStatusRole(targetStatusId);
    if (WORKFLOW_ROLES.has(role)) {
        openWorkflowModal({
            control,
            recall,
            role,
            targetStatusId,
        });
        return;
    }

    saveStatusPatchOptimistically(control, recall, targetStatusId, root);
}

/**
 * Binds Recalls inline edit controls.
 * @param {HTMLElement} container Recalls route container.
 * @param {Function|null} rerender Optional route rerender callback.
 * @returns {void}
 */
export function bindRecallsInlineEditActions(container, rerender = null) {
    inlineEditRerender = typeof rerender === "function" ? rerender : null;
    container.querySelectorAll("[data-recalls-inline-root]").forEach((root) => {
        bindInlineRoot(root);
    });

    container.querySelectorAll("select[data-recalls-inline-field]").forEach((control) => {
        control.addEventListener("change", () => {
            const recall = getRecallById(control.dataset.recallId);
            if (!recall?.id) {return;}

            if (control.dataset.recallsInlineField === "status") {
                handleStatusChange(control, recall);
            }
        });
    });
}
